import React, { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  DIAS_SEMANA,
  SELECTABLE_AR_EMAIL_STATUSES,
  DEFAULT_REPEAT_LAST_EVERY_DAYS,
} from '@claimup/shared/constants/email-templates';
import { STATES_THAT_IMPLY_JUDICIAL, expedientStatusLabel } from '@claimup/shared/constants/expedient';

// Un expediente AR nunca está en un estado judicial (judicializarlo lo saca de
// AR): ofrecerlos aquí eran 15 botones que no hacen nada.
const ESTADOS_DE_AR = SELECTABLE_AR_EMAIL_STATUSES.filter(e => !STATES_THAT_IMPLY_JUDICIAL.includes(e));

/**
 * Cuándo sale la secuencia automática de este cliente.
 *
 * **Lo que sustituye.** Hoy esto vive en tres `cron.schedule` de
 * `services/scripts/cronJobs.js` (`0 16 * * 1`, `0 12 * * 3`, `0 16 * * 4`:
 * lunes, miércoles y jueves) y es **igual para todos los clientes**. Cambiarlo
 * para uno solo obliga a tocar código y desplegar, y no hay ninguna pantalla
 * donde mirar qué recibe una cartera. Es el mismo problema que resolvió
 * `escalation_order`: lo que es distinto por cliente no puede vivir en una
 * constante.
 *
 * El interruptor va **arriba y separado** porque es la única pregunta que
 * importa de un vistazo: si esto está apagado, lo demás da igual.
 */

/**
 * Casilla en forma de pastilla. La usan los días y los estados porque son la
 * misma pregunta —¿entra o no entra?— y merecen el mismo gesto.
 */
const Chip = ({ etiqueta, marcado, onCambiar }) => (
  <label
    className={`px-2.5 py-1 rounded-lg text-xs cursor-pointer border ${
      marcado ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-500'
    }`}
  >
    <input type='checkbox' checked={marcado} onChange={onCambiar} className='sr-only' />
    {etiqueta}
  </label>
);

const CadenceCard = ({ cadencia, onGuardar }) => {
  const [borrador, setBorrador] = useState(cadencia);
  const [guardando, setGuardando] = useState(false);

  const sucio = JSON.stringify(borrador) !== JSON.stringify(cadencia);

  const cambiar = patch => setBorrador(b => ({ ...b, ...patch }));

  const alternarDia = dia =>
    cambiar({
      days: borrador.days.includes(dia)
        ? borrador.days.filter(d => d !== dia)
        : [...borrador.days, dia].sort((a, b) => a - b),
    });

  // Se conserva el orden en que los ofrece la lista, no el de los clics: así la
  // fila se lee igual siempre.
  const alternarEstado = estado =>
    cambiar({
      statuses: SELECTABLE_AR_EMAIL_STATUSES.filter(e =>
        e === estado ? !borrador.statuses.includes(e) : borrador.statuses.includes(e)
      ),
    });

  /**
   * El intervalo de insistencia, con el default por delante: el backend lo
   * rellena al leer, pero una respuesta antigua o un mock sin el campo dejarían
   * el desplegable sin valor y React lo pasaría a no controlado.
   */
  const insistencia = Number.isInteger(borrador.repeat_last_every_days)
    ? borrador.repeat_last_every_days
    : DEFAULT_REPEAT_LAST_EVERY_DAYS;

  const guardar = async () => {
    setGuardando(true);
    try {
      await onGuardar({
        enabled: borrador.enabled,
        days: borrador.days,
        send_hour: borrador.send_hour,
        statuses: borrador.statuses,
        stop_on_reply: borrador.stop_on_reply,
        // Viaja SIEMPRE, también cuando vale 0: si solo se mandara con valor,
        // poner «No insistir» no quitaría la insistencia — se quedaría la de
        // antes y la pantalla diría otra cosa.
        repeat_last_every_days: insistencia,
      });
    } finally {
      setGuardando(false);
    }
  };

  /**
   * Los intervalos que ofrece el desplegable, **más el que haya guardado** si no
   * está en la lista. Sin eso, una fila con 3 días —puesta por API— se pintaría
   * con otro valor seleccionado y al guardar se perdería sin que nadie lo
   * pidiera: el desplegable no puede cambiar un dato por no saber mostrarlo.
   */
  const intervalos = [...new Set([0, 7, 14, 21, 30, 45, 60, 90, insistencia])]
    .filter(d => Number.isInteger(d) && d >= 0)
    .sort((a, b) => a - b);

  // Encendida y sin días no manda nunca, y el backend lo rechaza. Se avisa aquí
  // para no tener que llegar al error.
  const sinDias = borrador.enabled && borrador.days.length === 0;
  const sinEstados = borrador.enabled && borrador.statuses.length === 0;
  const noMandaNunca = sinDias || sinEstados;

  return (
    <div className={`bg-white border rounded-lg p-4 ${borrador.enabled ? 'border-blue-200' : 'border-slate-200'}`}>
      <div className='flex items-start justify-between gap-3 mb-3'>
        <div>
          <h2 className='text-sm font-medium text-slate-800'>Cuándo se manda</h2>
          <p className='text-xs text-slate-500'>
            La secuencia automática de esta empresa. Mientras esté apagada no sale ningún correo solo, aunque haya
            escalera.
          </p>
        </div>
        <label className='flex items-center gap-2 text-sm shrink-0'>
          <input
            type='checkbox'
            checked={borrador.enabled}
            onChange={e => cambiar({ enabled: e.target.checked })}
            className='rounded-lg border-slate-300'
          />
          <span className={borrador.enabled ? 'text-blue-700 font-medium' : 'text-slate-500'}>
            {borrador.enabled ? 'Encendida' : 'Apagada'}
          </span>
        </label>
      </div>

      <fieldset className='mb-3'>
        <legend className='text-xs font-medium text-slate-600 mb-1'>Días</legend>
        <div className='flex flex-wrap gap-1.5'>
          {DIAS_SEMANA.map(([valor, etiqueta]) => (
            <Chip
              key={valor}
              etiqueta={etiqueta}
              marcado={borrador.days.includes(valor)}
              onCambiar={() => alternarDia(valor)}
            />
          ))}
        </div>
      </fieldset>

      <div className='flex flex-wrap items-end gap-4'>
        <div>
          <label htmlFor='cadencia-hora' className='block text-xs font-medium text-slate-600 mb-1'>
            A partir de las
          </label>
          <select
            id='cadencia-hora'
            value={borrador.send_hour}
            onChange={e => cambiar({ send_hour: Number(e.target.value) })}
            className='rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </div>

        <p className='text-xs text-slate-500 max-w-xs'>
          Hora de <strong>{borrador.timezone}</strong>. Es un suelo, no una cita: a partir de ahí la ventana queda
          abierta el resto del día.
        </p>
      </div>

      <fieldset className='mt-4'>
        <legend className='text-xs font-medium text-slate-600 mb-1'>Estados que reciben correo</legend>
        <p className='text-xs text-slate-500 mb-2'>
          Solo se escribe a expedientes en estos estados. Los <strong>finalizados</strong> no salen en la lista y no son
          configurables: escribirle a alguien que ya pagó, o a una dirección que rebotó, no es una preferencia.
        </p>
        <div className='flex flex-wrap gap-1.5'>
          {ESTADOS_DE_AR.map(estado => (
            <Chip
              key={estado}
              etiqueta={expedientStatusLabel(estado)}
              marcado={borrador.statuses.includes(estado)}
              onCambiar={() => alternarEstado(estado)}
            />
          ))}
        </div>
      </fieldset>

      <label className='flex items-start gap-2 mt-3 text-sm'>
        <input
          type='checkbox'
          checked={borrador.stop_on_reply}
          onChange={e => cambiar({ stop_on_reply: e.target.checked })}
          className='mt-0.5 rounded-lg border-slate-300'
        />
        <span className='text-slate-700'>
          Parar la secuencia si el deudor contesta
          <span className='block text-xs text-slate-500'>
            Seguir mandando avisos a alguien que ya está hablando contigo es la forma más rápida de que te marque como
            spam.
          </span>
        </span>
      </label>

      <div className='flex flex-wrap items-end gap-4 mt-4 pt-3 border-t border-slate-100'>
        <div>
          <label htmlFor='cadencia-insistencia' className='block text-xs font-medium text-slate-600 mb-1'>
            Al acabar la escalera, repetir el último cada
          </label>
          <select
            id='cadencia-insistencia'
            value={insistencia}
            onChange={e => cambiar({ repeat_last_every_days: Number(e.target.value) })}
            className='rounded-lg border border-slate-200 p-2 text-sm focus:border-blue-400 focus:outline-none'
          >
            {intervalos.map(d => (
              <option key={d} value={d}>
                {d === 0 ? 'No insistir' : `${d} días`}
              </option>
            ))}
          </select>
        </div>

        <p className='text-xs text-slate-500 max-w-sm'>
          {insistencia === 0 ? (
            <>
              Al agotar la escalera <strong>se deja de escribir</strong> a ese expediente.
            </>
          ) : (
            <>
              No se deja de insistir, pero con su propio ritmo y no con los días de arriba. El correo sale de{' '}
              <strong>tu dominio</strong>, y su reputación la comparte toda tu cartera: insistir a diario a quien no
              contesta manda a spam también el primer correo de los deudores nuevos.
            </>
          )}
        </p>
      </div>

      {noMandaNunca && (
        <p className='flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-3'>
          <ExclamationTriangleIcon className='w-3.5 h-3.5 shrink-0 mt-0.5' />
          Encendida y sin ningún {sinDias ? 'día' : 'estado'} no manda nunca. Elige al menos uno, o apágala.
        </p>
      )}

      {!cadencia.configured && !sucio && (
        <p className='text-xs text-slate-400 mt-3'>
          Todavía sin configurar: lo que ves son los días que usa recobro hoy, como punto de partida.
        </p>
      )}

      {sucio && (
        <div className='flex items-center gap-2 mt-3'>
          <button
            type='button'
            onClick={guardar}
            disabled={guardando || noMandaNunca}
            className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium ${
              guardando || noMandaNunca ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            type='button'
            onClick={() => setBorrador(cadencia)}
            disabled={guardando}
            className='text-xs text-slate-500 hover:text-slate-700'
          >
            Descartar
          </button>
        </div>
      )}
    </div>
  );
};

export default CadenceCard;
